import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { useEdmDocumentationOperationalScopeCountsQuery } from 'graphql-common';
import { TabBarItem as TabBarItemType } from '@lib/components/TabBar/constants';
import TabBar from '@lib/components/TabBar/TabBar';
import getOperationalScopeTabs from './utils/getOperationalScopeTabs';
import AreasTab from './AreasTab';
import EquipmentTab from './EquipmentTab';
import FormTemplatesTab from './FormTemplatesTab';
import TasksTab from './TasksTab';

const TABS = {
  equipment: 'equipment',
  areas: 'areas',
  formTemplates: 'formTemplates',
  tasks: 'tasks',
};

interface DocumentViewOperationalScopeProps {
  documentId: string;
  isAssetsAvailable: boolean;
  isFullAdmin?: boolean;
  isSitesAvailable: boolean;
  isTasksAvailable: boolean;
  isViewLocked?: boolean;
}

export default function DocumentViewOperationalScope({
  documentId,
  isAssetsAvailable,
  isFullAdmin,
  isSitesAvailable,
  isTasksAvailable,
  isViewLocked,
}: DocumentViewOperationalScopeProps) {
  const { t } = useTranslation();
  // Non-Full-Admin Users on a locked plan can only act on assets; collapse
  // the operational-scope view to just the Equipment table — no subtabs,
  // no counters query (the locked sub-resolvers would also fail or render
  // useless empty states).
  const restrictedToAssets = !!isViewLocked && !isFullAdmin;
  const hasAnyTab = isAssetsAvailable || isSitesAvailable || isTasksAvailable;

  const { data: countsData, refetch: refetchCounts } =
    useEdmDocumentationOperationalScopeCountsQuery({
      // Per-component access (e.g. LimitedRead on TasksManagement) makes those
      // root resolvers throw FORBIDDEN. Their fields are non-null in the
      // schema, so the error propagates to the root and nukes the whole
      // response. We use @include directives to drop the inaccessible
      // subselections client-side so the server never tries to resolve them.
      variables: {
        documentationId: documentId,
        includeAssets: isAssetsAvailable,
        includeAreas: isSitesAvailable,
        includeTasks: isTasksAvailable,
        isViewLocked,
      },
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'ignore',
      skip: !documentId || !hasAnyTab || restrictedToAssets,
    });

  const tabs = useMemo(
    () =>
      getOperationalScopeTabs({
        t,
        isAssetsAvailable,
        isSitesAvailable,
        isTasksAvailable,
        equipmentCount: _get(countsData, 'assets.metadata.totalCount', 0),
        areasCount: _get(countsData, 'siteAreas.metadata.totalCount', 0),
        formTemplatesCount: _get(
          countsData,
          'formTemplates.metadata.totalCount',
          0,
        ),
        tasksCount: _get(countsData, 'tasks.metadata.totalCount', 0),
      }),
    [countsData, isAssetsAvailable, isSitesAvailable, isTasksAvailable, t],
  );

  const [activeSubTab, setActiveSubTab] = useState(
    () => tabs[0]?.value ?? TABS.equipment,
  );

  useEffect(() => {
    if (!tabs.length) return;
    if (tabs.some((tab) => tab.value === activeSubTab)) return;
    setActiveSubTab(tabs[0].value);
  }, [tabs, activeSubTab]);

  const onActiveChange = useCallback(
    ({ value }: TabBarItemType) => {
      if (value === activeSubTab) return;
      setActiveSubTab(value);
      refetchCounts();
    },
    [activeSubTab, refetchCounts],
  );

  if (restrictedToAssets) {
    return <EquipmentTab documentId={documentId} />;
  }

  if (tabs.length === 0) return null;

  return (
    <>
      <TabBar
        items={tabs}
        active={activeSubTab}
        onActiveChange={onActiveChange}
      />
      {activeSubTab === TABS.equipment && (
        <EquipmentTab documentId={documentId} />
      )}
      {activeSubTab === TABS.areas && <AreasTab documentId={documentId} />}
      {activeSubTab === TABS.formTemplates && (
        <FormTemplatesTab documentId={documentId} />
      )}
      {activeSubTab === TABS.tasks && <TasksTab documentId={documentId} />}
    </>
  );
}
